import { useEffect, useState } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { FilesField } from '../../../../components/inputs/FilesField';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { formatBytes } from '../../../../lib/formatBytes';
import { focusValidationError } from '../../../../lib/validationFocus';
import { accessLevelsApi } from '../../../administration/access-levels/accessLevelsApi';
import type { AccessLevel } from '../../../administration/access-levels/accessLevelTypes';
import { caseFilesApi, casesApi } from '../casesApi';
import {
  CASE_FILE_ACCEPT,
  CASE_FILE_MAX_BYTES,
  CASE_STATUS,
  CASE_STATUS_LABEL,
  type CaseListItem,
  type CaseStatus,
} from '../caseTypes';
import { fileAll } from '../utils/fileAll';
import { sampleCase, samplePdf } from '../utils/sampleCase';

const NONE = 'none';

/**
 * Registers a case, or corrects one. A registration may carry files; an edit never does.
 *
 * <b>The case profile is three fields for now</b> — number, title, status — product call,
 * 07 Oct 2026, pending the NBI case form. The number is TYPED, because the bureau issues it.
 *
 * <b>Classifying rides on the CREATE only</b>, and only for a reader holding
 * `cases.set-access-level` — the server checks the same key in the controller, because a
 * key one request shape gets around is not a key. An edit never carries a level: a PUT rewrites
 * every column it names, and a reader without the key would have every edit refused.
 */
export function CaseModal({
  editing,
  onClose,
  onSaved,
}: {
  editing: CaseListItem | null;
  onClose: () => void;
  /** `failed` names the files that did not file; the case itself was registered. */
  onSaved: (saved: CaseListItem, failed: { name: string; reason: string }[]) => void;
}) {
  const canClassify = usePermission('cases.set-access-level');
  const classifies = canClassify && editing === null;

  // Files ride on the REGISTRATION only, for a reader holding the key; an edit manages them on the
  // case's Attachments tab.
  const canAttach = usePermission('cases.attach');
  const takesFiles = canAttach && editing === null;

  const [caseNumber, setCaseNumber] = useState(editing?.caseNumber ?? '');
  const [title, setTitle] = useState(editing?.title ?? '');
  const [status, setStatus] = useState<string>(String(editing?.status ?? CASE_STATUS.ongoing));
  const [levelId, setLevelId] = useState<string>(NONE);
  const [files, setFiles] = useState<File[]>([]);
  const [progress, setProgress] = useState<number | null>(null);

  const [levels, setLevels] = useState<AccessLevel[]>([]);
  const [loadingLevels, setLoadingLevels] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!classifies) return;

    const controller = new AbortController();
    setLoadingLevels(true);

    accessLevelsApi
      .options(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setLevels(next);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setLoadingLevels(false);
      });

    return () => controller.abort();
  }, [classifies]);

  const oversize = files.some((file) => file.size > CASE_FILE_MAX_BYTES);
  const valid = caseNumber.trim().length > 0 && title.trim().length > 0 && !oversize;
  const canSave = valid && confirmed && !saving;
  const chosenLevel = levels.find((l) => l.id === levelId) ?? null;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const fields = {
      caseNumber: caseNumber.trim(),
      title: title.trim(),
      status: Number(status) as CaseStatus,
    };

    try {
      const saved = editing
        ? await casesApi.update(editing.id, fields)
        : await casesApi.create({ ...fields, accessLevelId: classifies && levelId !== NONE ? levelId : null });

      /*
        The case FIRST, then each file in its own request: a file that fails does not undo the
        registration — resubmitting would register the case twice. What failed is named.
      */
      let failed: { name: string; reason: string }[] = [];

      if (takesFiles && files.length > 0) {
        setProgress(0);
        failed = await fileAll(caseFilesApi(saved.id), files, setProgress);
      }

      onSaved(saved, failed);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the case.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'case' });
    } finally {
      setSaving(false);
      setProgress(null);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? `Edit ${editing.caseNumber}` : 'Register case'}
      size="lg"
      id="case-modal"
      autogenerate={{
        onGenerate: () => {
          const sample = sampleCase();
          setCaseNumber(sample.caseNumber);
          setTitle(sample.title);
          if (takesFiles) setFiles([samplePdf('sample-case-document.pdf')]);

          // The level stays at its default, Unclassified — a generated case classified out of
          // its own creator's reach would be a sample nobody could open.
        },
        disabled: saving,
      }}
      confirm={{ id: 'case-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id="case-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="case-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel={progress === null ? 'Submitting' : `Filing ${progress}%`}
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="case-case-number"
            label="Case number"
            required
            description="The number the bureau issued. Typed as written; unique among live cases."
            value={caseNumber}
            onChange={(e) => setCaseNumber(e.target.value)}
            maxLength={60}
            disabled={saving}
            error={error?.fieldError('caseNumber')}
          />

          <Select
            id="case-status"
            label="Case status"
            required
            value={status}
            onChange={setStatus}
            sort="none"
            clearable={false}
            disabled={saving}
            options={Object.values(CASE_STATUS).map((value) => ({
              value: String(value),
              label: CASE_STATUS_LABEL[value],
            }))}
            error={error?.fieldError('status')}
          />
        </div>

        <TextField
          id="case-title"
          label="Case title"
          required
          description="What the case is called. A reader below the case's access level sees asterisks in its place."
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={300}
          disabled={saving}
          error={error?.fieldError('title')}
        />

        {classifies ? (
          <>
            <Select
              id="case-access-level-id"
              label="Access level"
              description="The clearance a member needs to open this case and its evidence."
              value={levelId}
              onChange={(next) => setLevelId(next || NONE)}
              loading={loadingLevels}
              disabled={saving}
              sort="none"
              clearable={false}
              options={[
                { value: NONE, label: 'Unclassified', sublabel: 'Readable by every member of the case' },
                ...levels.map((level) => ({
                  value: level.id,
                  label: level.name,
                  sublabel: level.description ?? undefined,
                  right: <span className="font-mono tabular-nums">Rank {level.rank}</span>,
                })),
              ]}
              error={error?.fieldError('accessLevelId')}
            />

            {/* The consequence, before the tick, in the case's own terms. */}
            <p className="text-xs text-slate-500">
              {chosenLevel
                ? `Only members tagged ${chosenLevel.name} or stronger will open this case. Other members still see it listed, with the title masked.`
                : 'Every member of the case will be able to open it.'}{' '}
              If your own clearance does not reach the level you choose, you will not be able to
              open the case once it is created.
            </p>
          </>
        ) : null}

        {takesFiles ? (
          <FilesField
            id="case-files"
            label="Attachments"
            description={`PDF, PNG, JPEG or MP4, up to ${formatBytes(CASE_FILE_MAX_BYTES)} each. Filed after the case is registered, one at a time.`}
            accept={CASE_FILE_ACCEPT}
            maxBytes={CASE_FILE_MAX_BYTES}
            files={files}
            onChange={setFiles}
            disabled={saving}
          />
        ) : null}
      </div>
    </Modal>
  );
}
